// ===============================================
// 1. MobileFilterDrawer.jsx - NEW COMPONENT
// ===============================================

import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Rating,
  FormControlLabel,
  Checkbox,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  ClearAll as ClearAllIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const MobileFilterDrawer = ({ 
  open, 
  onClose, 
  category, 
  setCategory, 
  categoryOptions,
  maxDistance,
  setMaxDistance,
  ratingFilter,
  setRatingFilter,
  showInsidersOnly,
  setShowInsidersOnly,
  handleDiscoverFromNetwork,
  isAuthenticated,
  userCoordinates,
  hasActiveFilters,
  onResetFilters,
  DISTANCE_OPTIONS
}) => {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          overflow: 'auto'
        }
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Filters</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasActiveFilters() && (
              <Button
                size="small"
                onClick={onResetFilters}
                startIcon={<ClearAllIcon />}
              >
                Clear All
              </Button>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Category Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
            Category
          </Typography>
          <FormControl fullWidth>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Select business category"
            >
              {categoryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{option.label}</Typography>
                    <Typography color="text.secondary">({option.count})</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Distance Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
            Distance
          </Typography>
          <FormControl fullWidth>
            <Select
              value={maxDistance}
              onChange={(e) => setMaxDistance(e.target.value)}
              disabled={userCoordinates.loading}
              aria-label="Select maximum distance"
            >
              {DISTANCE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {userCoordinates.error && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {userCoordinates.error}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Rating Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
            Minimum Rating
          </Typography>
          <Box sx={{ px: 1 }}>
            <Rating
              value={ratingFilter}
              onChange={(e, newValue) => setRatingFilter(newValue)}
              precision={0.5}
              size="large"
              aria-label="Select minimum rating filter"
            />
          </Box>
        </Box>

        {/* Insider Connections - PRESERVING EXISTING CACHE FUNCTIONALITY */}
        <Collapse in={isAuthenticated}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInsidersOnly}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setShowInsidersOnly(isChecked);
                  handleDiscoverFromNetwork(isChecked); // EXISTING CACHE LOGIC
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">
                  Show Network Connections Only
                </Typography>
                <Tooltip title="Shows results from businesses connected through your network">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
            sx={{ mb: 2 }}
          />
        </Collapse>

        {/* Apply Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{ mt: 2, py: 1.5 }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
};

export default MobileFilterDrawer;